import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Notification,
    Page,
    PageContent,
    Select,
    Text,
    TextInput,
} from 'grommet';
import { StatusCritical, StatusGood } from 'grommet-icons';
import { redirect, useLoaderData } from 'react-router-dom';
import {
    API,
    SearchSelect,
    hasToken,
    hasUserId,
    UserService,
    AppConstants,
    userIsCoach,
    userIsAdmin,
} from '@enrich/shared-lib';
import QueryString from 'qs';

export async function inviteAdminLoader() {
    if (!hasToken() || !hasUserId()) {
        return redirect('/login');
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    try {
        const user = await new UserService().getUser(userId);
        if (!user || (!userIsCoach(user) && !userIsAdmin(user))) {
            return redirect('/');
        }
        return user;
    } catch {
        return redirect('/login');
    }
}

const PORTAL_ROLES = ['company admin', 'school admin'];
const WELCOME_CALL_COACH_ROLE = 'welcome call coach';
const ALL_ROLES = [...PORTAL_ROLES, WELCOME_CALL_COACH_ROLE];

const isPortalRole = (role) => PORTAL_ROLES.includes(role);

const defaultValues = { email: '', firstName: '', lastName: '', role: '', entitySlug: '' };

export const InviteAdmin = () => {
    const user = useLoaderData();
    const isAdmin = userIsAdmin(user);

    const [value, setValue] = useState(defaultValues);
    const [slugOptions, setSlugOptions] = useState([]);
    const [buttonBusy, setButtonBusy] = useState(false);
    const [successToast, setSuccessToast] = useState(false);
    const [failureToast, setFailureToast] = useState({
        visible: false,
        message: '',
    });

    const roleOptions = isAdmin ? ALL_ROLES : PORTAL_ROLES;
    const isWelcomeCallCoach = value.role === WELCOME_CALL_COACH_ROLE;
    const needsEntity = isPortalRole(value.role);

    useEffect(() => {
        if (!needsEntity) {
            setSlugOptions([]);
            return;
        }

        let isActive = true;

        (async () => {
            const contentType =
                value.role === 'company admin' ? 'companies' : 'schools';
            const query = QueryString.stringify({
                fields: ['slug', 'name'],
                pagination: { limit: 500 },
            });
            try {
                const response = await API.get(`/api/${contentType}?${query}`);
                if (isActive) {
                    setSlugOptions(
                        (response.data.data ?? [])
                            .map((item) => ({
                                slug: item.attributes.slug,
                                name: item.attributes.name,
                            }))
                            .sort((a, b) => a.name.localeCompare(b.name)),
                    );
                }
            } catch (error) {
                console.log(error);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [value.role, needsEntity]);

    const handleEntitySelect = useCallback(
        (item) =>
            setValue((prev) => ({
                ...prev,
                entitySlug: item?.slug ?? '',
            })),
        [],
    );

    const handleFormChange = (nextValue) => {
        if (nextValue.role !== value.role) {
            setValue({ ...nextValue, entitySlug: '' });
        } else {
            setValue(nextValue);
        }
    };

    const handleSubmit = async () => {
        setButtonBusy(true);
        try {
            if (isWelcomeCallCoach) {
                await API.post('/api/users/invite-coach', {
                    email: value.email,
                    firstName: value.firstName,
                    lastName: value.lastName,
                });
            } else {
                await API.post('/api/users/invite-admin', {
                    email: value.email,
                    role: value.role,
                    entitySlug: value.entitySlug,
                });
            }
            setSuccessToast(true);
            setValue(defaultValues);
            setSlugOptions([]);
        } catch (error) {
            console.log(error);
            setFailureToast({
                visible: true,
                message: error?.message ?? '',
            });
        }
        setButtonBusy(false);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent align="center">
                <Box width="medium">
                    <Heading level="2" margin={{ bottom: 'none' }}>
                        Invite User
                    </Heading>
                    <Text margin={{ bottom: 'medium' }} color="dark-5">
                        Send an invitation to a new platform user.
                    </Text>
                    <Form
                        value={value}
                        onChange={handleFormChange}
                        onSubmit={handleSubmit}>
                        <Box gap="medium">
                            <FormField
                                label="Role"
                                name="role"
                                htmlFor="invite-role-input"
                                required>
                                <Select
                                    id="invite-role-input"
                                    name="role"
                                    options={roleOptions}
                                    value={value.role}
                                    onChange={({ option }) =>
                                        handleFormChange({
                                            ...value,
                                            role: option,
                                        })
                                    }
                                    placeholder="Select a role"
                                />
                            </FormField>
                            <FormField
                                label="Email"
                                name="email"
                                htmlFor="invite-email-input"
                                required>
                                <TextInput
                                    id="invite-email-input"
                                    name="email"
                                    type="email"
                                />
                            </FormField>
                            {isWelcomeCallCoach && (
                                <>
                                    <FormField
                                        label="First Name"
                                        name="firstName"
                                        htmlFor="invite-firstname-input"
                                        required>
                                        <TextInput
                                            id="invite-firstname-input"
                                            name="firstName"
                                        />
                                    </FormField>
                                    <FormField
                                        label="Last Name"
                                        name="lastName"
                                        htmlFor="invite-lastname-input"
                                        required>
                                        <TextInput
                                            id="invite-lastname-input"
                                            name="lastName"
                                        />
                                    </FormField>
                                </>
                            )}
                            {needsEntity && (
                                <FormField
                                    label="Entity"
                                    name="entitySlug"
                                    htmlFor="invite-slug-input"
                                    required>
                                    <SearchSelect
                                        options={slugOptions}
                                        labelKey="name"
                                        placeholder="Select an entity"
                                        updateFormData={handleEntitySelect}
                                        defaultValue={slugOptions.find(
                                            (o) => o.slug === value.entitySlug,
                                        )}
                                        disabled={!value.role}
                                    />
                                </FormField>
                            )}
                            <Button
                                type="submit"
                                primary
                                label="Send Invitation"
                                busy={buttonBusy}
                                disabled={!value.role}
                                margin={{ top: 'small' }}
                            />
                        </Box>
                    </Form>
                </Box>
            </PageContent>
            {successToast && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="normal"
                    time={8000}
                    title="Invitation sent successfully!"
                    icon={<StatusGood color="green" />}
                    onClose={() => setSuccessToast(false)}
                />
            )}
            {failureToast.visible && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="critical"
                    time={8000}
                    title="Failed to send invitation"
                    message={failureToast.message || undefined}
                    icon={<StatusCritical color="red" />}
                    onClose={() =>
                        setFailureToast({ visible: false, message: '' })
                    }
                />
            )}
        </Page>
    );
};
